import { Link } from 'react-router-dom';
import { Star, Shield, Clock, TrendingDown, CheckCircle, HelpCircle, Users, FileText, MapPin, Scale } from 'lucide-react';
import { CITIES_DATA, ADVANTAGES, PROCESS_STEPS, COMPARISON_ITEMS, SWISS_FEATURES, TESTIMONIALS, FAQ_ITEMS } from '../../data/landingData';

const ICON_MAP = { TrendingDown, Shield, Clock, Scale, FileText, MapPin };

export function TrustBar() {
  const items = [
    { id: 'clients', icon: Users, value: "98'000+", label: 'Clients satisfaits' },
    { id: 'lpd', icon: Shield, value: '100%', label: 'Données protégées (LPD)' },
    { id: 'delai', icon: Clock, value: '48h', label: 'Délai de traitement' },
    { id: 'savings', icon: TrendingDown, value: '20%', label: "D'économies moyennes" },
  ];
  return (
    <section className="bg-[#F9FAFB] border-b border-gray-200 py-10" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {items.map(({ id, icon: Icon, value, label }) => (
            <div key={id} className="animate-fade-in-up">
              <Icon className="mx-auto mb-2 text-[#E3000F]" size={28} />
              <p className="font-['Chivo'] font-black text-2xl sm:text-3xl text-[#111827]">{value}</p>
              <p className="text-sm text-gray-600 font-['IBM_Plex_Sans']">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AdvantagesSection() {
  const icons = [TrendingDown, Shield, Clock];
  return (
    <section id="avantages" className="py-20 sm:py-24 bg-white border-b border-gray-200" data-testid="advantages-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Vos avantages</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12 max-w-2xl">{"Votre expert fiscal s'occupe de tout."}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {ADVANTAGES.map((adv, idx) => {
            const Icon = icons[idx];
            return (
              <div key={adv.id} className="border border-gray-200 p-8 hover:border-[#E3000F] transition-colors">
                <div className="w-12 h-12 bg-[#E3000F] flex items-center justify-center mb-6"><Icon className="text-white" size={24} /></div>
                <h3 className="font-['Chivo'] font-bold text-xl text-[#111827] mb-3">{adv.title}</h3>
                <p className="text-gray-600 font-['IBM_Plex_Sans'] leading-relaxed">{adv.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ProcessSection() {
  return (
    <section className="py-20 sm:py-24 bg-[#F9FAFB] border-b border-gray-200" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Comment ça fonctionne</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12 max-w-2xl">{"En 4 étapes simples, c'est réglé."}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((s) => (
            <div key={s.id} className="border border-gray-200 bg-white p-6">
              <span className="font-['Chivo'] font-black text-4xl text-[#E3000F] opacity-30">{s.step}</span>
              <h3 className="font-['Chivo'] font-bold text-lg text-[#111827] mt-2 mb-2">{s.title}</h3>
              <p className="text-sm text-gray-600 font-['IBM_Plex_Sans'] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonCell({ value }) {
  if (value === true) return <CheckCircle size={18} className="mx-auto text-[#16A34A]" />;
  return <HelpCircle size={18} className="mx-auto text-gray-400" />;
}

export function ComparisonSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-b border-gray-200" data-testid="comparison-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Comparaison</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12">Pourquoi confier sa déclaration à un professionnel ?</h2>
        <div className="border border-gray-200 overflow-hidden">
          <table className="w-full" data-testid="comparison-table">
            <thead>
              <tr className="bg-[#F9FAFB]">
                <th className="text-left px-6 py-4 font-['Chivo'] font-bold text-sm text-[#111827] border-b border-gray-200 w-1/2" />
                <th className="text-center px-4 py-4 font-['Chivo'] font-bold text-sm text-gray-500 border-b border-gray-200">Faire soi-même</th>
                <th className="text-center px-4 py-4 font-['Chivo'] font-bold text-sm text-[#E3000F] border-b border-gray-200">Expert fiscal</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ITEMS.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'}>
                  <td className="px-6 py-3 text-sm text-[#111827] font-['IBM_Plex_Sans'] border-b border-gray-100">{item.text}</td>
                  <td className="text-center px-4 py-3 border-b border-gray-100"><ComparisonCell value={item.selfDone} /></td>
                  <td className="text-center px-4 py-3 border-b border-gray-100"><CheckCircle size={18} className="mx-auto text-[#16A34A]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export function SwissSpecificsSection() {
  const icons = [Scale, FileText, MapPin, Shield];
  return (
    <section className="py-20 sm:py-24 bg-[#111827] text-white border-b border-gray-200" data-testid="swiss-specifics-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Spécificités suisses</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight mb-6">{"La fiscalité suisse est un labyrinthe. Ne vous y perdez pas."}</h2>
        <p className="text-gray-400 font-['IBM_Plex_Sans'] leading-relaxed mb-12 max-w-3xl">
          {"26 cantons, 26 systèmes fiscaux différents. Chaque commune applique son propre multiplicateur d'impôt. Les règles changent chaque année. Un expert local connaît les subtilités de votre canton — et c'est ce qui fait la différence entre payer le juste prix et payer trop."}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SWISS_FEATURES.map((feat, idx) => {
            const Icon = icons[idx];
            return (
              <div key={feat.id} className="border border-gray-700 p-6 hover:border-[#E3000F] transition-colors">
                <Icon className="text-[#E3000F] mb-4" size={28} />
                <h3 className="font-['Chivo'] font-bold text-lg text-white mb-2">{feat.title}</h3>
                <p className="text-gray-400 font-['IBM_Plex_Sans'] text-sm leading-relaxed">{feat.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function CitiesSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-b border-gray-200" data-testid="cities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Votre région</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12">Un expert fiscal près de chez vous</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(CITIES_DATA).map(([key, data]) => (
            <Link key={key} to={`/ville/${key}`} className="border border-gray-200 p-5 hover:border-[#E3000F] transition-colors group" data-testid={`city-link-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={16} className="text-[#E3000F]" />
                <span className="font-['Chivo'] font-bold text-[#111827] group-hover:text-[#E3000F] transition-colors">{data.name}</span>
              </div>
              <p className="text-xs text-gray-500 font-['IBM_Plex_Sans']">Canton {data.canton} — {data.population} hab.</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="py-20 sm:py-24 bg-[#F9FAFB] border-b border-gray-200" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Témoignages</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12">Ils nous font confiance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.id} className="border border-gray-200 bg-white p-6">
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map((n) => <Star key={`${t.id}-star-${n}`} size={14} className="fill-[#E3000F] text-[#E3000F]" />)}
              </div>
              <p className="text-sm text-gray-700 font-['IBM_Plex_Sans'] leading-relaxed mb-4 italic">{`"${t.text}"`}</p>
              <div className="border-t border-gray-100 pt-3">
                <p className="text-sm font-bold text-[#111827]">{t.name}</p>
                <p className="text-xs text-gray-500">{t.city}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-b border-gray-200" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">Questions fréquentes</p>
        <h2 className="font-['Chivo'] font-black text-2xl sm:text-3xl lg:text-4xl text-[#111827] tracking-tight mb-12">Vos questions, nos réponses</h2>
        <div className="space-y-0">
          {FAQ_ITEMS.map((item) => (
            <div key={item.id} className="border-b border-gray-200 py-6">
              <h3 className="font-['Chivo'] font-bold text-lg text-[#111827] mb-2">{item.q}</h3>
              <p className="text-gray-600 font-['IBM_Plex_Sans'] leading-relaxed text-sm">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
